import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { BomImportState, BomImportStore } from './bom-import.store';
import { DropdownComponent, ItemSelectedEvent } from '../../dropdown/dropdown.component';
import { TreeItem, TreeSummaryComponent } from '../../tree-summary/tree-summary.component';
import { BomImportFormatDto } from './models/bom-import-format.dto';
import { BomImportPictureDto, BomImportPreviewDto } from './models/bom-import-preview.dto';
import { flattenPreview, PreviewRow, toTreeItem } from './models/bom-import.view-model';
import { formatAmount } from '../../../utils/format-amount';

@Component({
  selector: 'bom-import-page',
  templateUrl: './bom-import.page.html',
  styleUrl: './bom-import.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DropdownComponent, TreeSummaryComponent]
})
// One card in two states: pick a file (the preview request goes out as soon as one is chosen), then look at what
// will be created and import it under a name. No editing of the tree here — that happens on the tree page after
export class BomImportPage implements OnInit, OnDestroy {

  readonly state$: Observable<BomImportState>;
  readonly formatAmount = formatAmount;

  constructor(
    private store: BomImportStore,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    this.store.init().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  accept(state: BomImportState): string {
    return state.selectedFormat?.extensions.join(',') ?? '';
  }

  searchFormats = (searchTerm: string) => of(
    (this.store.state.formats ?? []).filter(format => format.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  formatFormat = (format: BomImportFormatDto) => format.name;

  onFormatSelected(event: ItemSelectedEvent<BomImportFormatDto>): void {
    if (!event.item) {
      return;
    }

    this.store.selectFormat(event.item);
  }

  onFilePicked(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    fileInput.value = ''; // re-picking the same file must fire (change) again

    if (!file) {
      return;
    }

    this.store.preview(file).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // "Kramer 2500 2000.xlsx — 23 elementy (21 nowych, 2 istniejące), 24 powiązania"
  summaryLine(preview: BomImportPreviewDto): string {
    const { newItems, existingItems, relations } = preview.counts;
    const items = newItems + existingItems;

    return `${plural(items, 'element', 'elementy', 'elementów')} `
      + `(${plural(newItems, 'nowy', 'nowe', 'nowych')}, ${plural(existingItems, 'istniejący', 'istniejące', 'istniejących')}), `
      + plural(relations, 'powiązanie', 'powiązania', 'powiązań');
  }

  // the root row is the product as typed, so the tree always shows what Importuj will create (0087)
  rows(preview: BomImportPreviewDto, state: BomImportState): PreviewRow[] {
    const [root, ...rest] = flattenPreview(preview.tree);

    return [{ ...root, name: state.rootName, partNumber: state.rootPartNumber || undefined, description: undefined }, ...rest];
  }

  summaryItems(preview: BomImportPreviewDto): TreeItem[] {
    return [toTreeItem(preview.tree)];
  }

  // "412,5 kg" — printed like every amount (0111); "—" for a row the sheet gave no mass
  formatMass(massKg: number | undefined): string {
    return massKg == null ? '—' : formatAmount(massKg, 'kg');
  }

  // "zdjęcie główne z arkusza · 259 KB" — in the file line
  pictureCaption(picture: BomImportPictureDto): string {
    return `zdjęcie główne z arkusza · ${Math.round(picture.sizeBytes / 1024)} KB`;
  }

  // the same, sentence-cased, as the picture's alt and tooltip
  pictureLabel(picture: BomImportPictureDto): string {
    return `Zdjęcie główne z arkusza · ${Math.round(picture.sizeBytes / 1024)} KB`;
  }

  onRootNameInput(value: string): void {
    this.store.setRootName(value);
  }

  onRootPartNumberInput(value: string): void {
    this.store.setRootPartNumber(value);
  }

  canImport(state: BomImportState): boolean {
    return !state.importing && state.rootName.trim().length > 0;
  }

  onImportClick(): void {
    this.store.import().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rootBomItemId => {
      if (rootBomItemId !== undefined) {
        this.router.navigate(['/bom', rootBomItemId]);
      }
    });
  }

  onCancelClick(): void {
    this.store.cancel();
  }
}

// Polish plural forms: 1 element, 2–4 elementy, 5+ elementów (and 22 elementy, 25 elementów)
function plural(count: number, one: string, few: string, many: string): string {
  const lastDigit = count % 10;
  const lastTwo = count % 100;
  const form = count === 1 ? one
    : lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14) ? few
    : many;

  return `${count} ${form}`;
}
